import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Package, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useUserOptions } from "@/modules/users/hooks/useUsers";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import {
  useCreateIssue,
  useIssueProducts,
  useProductModules,
  useUpdateIssue,
} from "../hooks/useIssues";
import type { IssueDetail, IssueListItem } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (code: string) => void;
  /** Edit mode — PUTs to the existing issue. */
  editIssue?: IssueDetail;
  /** Duplicate mode — POSTs a new issue prefilled from this record. */
  duplicateFrom?: IssueListItem;
};

export function NewIssueDrawer({
  isOpen,
  onClose,
  onSaved,
  editIssue,
  duplicateFrom,
}: Props) {
  const createIssue = useCreateIssue();
  const updateIssue = useUpdateIssue(editIssue?.id ?? "");
  const isEdit = !!editIssue;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [productId, setProductId] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [submoduleId, setSubmoduleId] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("new");
  const [assigneeId, setAssigneeId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: products = [] } = useIssueProducts();
  const { data: modules = [] } = useProductModules(productId || null);
  const { data: allUsers = [] } = useUserOptions();

  const { data: categoryItems = [] } = useCategoryItems("issue_category");
  const { data: priorityItems = [] } = useCategoryItems("issue_priority");
  const { data: statusItems = [] } = useCategoryItems("issue_status");

  // Module dropdown: active modules + the currently-selected one even if it
  // was later deactivated (so historical issues keep displaying it).
  const moduleOptions = useMemo(() => {
    const active = modules.filter((m) => m.isActive);
    const selected = modules.find((m) => m.id === moduleId);
    if (selected && !selected.isActive) return [selected, ...active];
    return active;
  }, [modules, moduleId]);

  const submoduleOptions = useMemo(() => {
    const mod = modules.find((m) => m.id === moduleId);
    if (!mod) return [];
    const active = mod.submodules.filter((s) => s.isActive);
    const selected = mod.submodules.find((s) => s.id === submoduleId);
    if (selected && !selected.isActive) return [selected, ...active];
    return active;
  }, [modules, moduleId, submoduleId]);

  // Prefill for edit / duplicate.
  useEffect(() => {
    if (!isOpen) return;
    const src = editIssue ?? duplicateFrom;
    if (src) {
      setTitle(src.title ?? "");
      // description only exists on IssueDetail (edit); duplicateFrom is a list item.
      setDescription(editIssue?.description ?? "");
      setProductId(src.productId ?? "");
      setModuleId(src.moduleId ?? "");
      setSubmoduleId(src.submoduleId ?? "");
      setCategory(src.category ?? "");
      setPriority(src.priority ?? "");
      // Duplicate resets status to New; edit keeps existing status.
      setStatus(editIssue ? (src.status ?? "new") : "new");
      setAssigneeId(src.assigneeId ?? "");
    } else {
      reset();
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editIssue, duplicateFrom]);

  function reset() {
    setTitle("");
    setDescription("");
    setProductId("");
    setModuleId("");
    setSubmoduleId("");
    setCategory("");
    setPriority("");
    setStatus("new");
    setAssigneeId("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !productId) return;
    setError(null);

    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      category: category || null,
      priority: priority || null,
      status,
      productId,
      moduleId: moduleId || null,
      submoduleId: submoduleId || null,
      assigneeId: assigneeId || null,
    };

    try {
      if (isEdit) {
        const res = await updateIssue.mutateAsync(payload);
        onSaved?.(res.issue.code);
      } else {
        const res = await createIssue.mutateAsync(payload);
        onSaved?.(res.issue.code);
      }
      if (!isEdit) reset();
      onClose();
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to save issue");
    }
  }

  const isPending = createIssue.isPending || updateIssue.isPending;

  if (!isOpen) return null;

  const heading = isEdit
    ? "Edit Issue"
    : duplicateFrom
      ? "Duplicate Issue"
      : "New Issue";

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div>
            <h2 className="text-lg font-semibold text-foreground">{heading}</h2>
            {duplicateFrom && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Duplicating from {duplicateFrom.code} — {duplicateFrom.title}
              </p>
            )}
            {isEdit && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {editIssue!.code} · Reported by {editIssue!.reportedBy?.name}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex-1 space-y-6 p-6">
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}

            {/* Classification */}
            <h3 className="text-sm font-semibold text-foreground">Classification</h3>

            <div className="space-y-2">
              <Label htmlFor="iss-product">
                Product <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="iss-product"
                  value={productId}
                  onChange={(e) => {
                    setProductId(e.target.value);
                    setModuleId("");
                    setSubmoduleId("");
                  }}
                  required
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Select a product...</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="iss-module">Module</Label>
                <Select
                  id="iss-module"
                  value={moduleId}
                  onChange={(e) => {
                    setModuleId(e.target.value);
                    setSubmoduleId("");
                  }}
                  disabled={!productId}
                >
                  <option value="">
                    {productId ? "No module" : "Select a product first"}
                  </option>
                  {moduleOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {!m.isActive ? " (inactive)" : ""}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="iss-submodule">Submodule / Page</Label>
                <Select
                  id="iss-submodule"
                  value={submoduleId}
                  onChange={(e) => setSubmoduleId(e.target.value)}
                  disabled={!moduleId}
                >
                  <option value="">
                    {moduleId ? "No submodule" : "Select a module first"}
                  </option>
                  {submoduleOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {!s.isActive ? " (inactive)" : ""}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="iss-category">Category</Label>
                <Select
                  id="iss-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">None</option>
                  {categoryItems.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="iss-priority">Priority</Label>
                <Select
                  id="iss-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="">None</option>
                  {priorityItems.map((p) => (
                    <option key={p.id} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="iss-status">Status</Label>
                <Select
                  id="iss-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {statusItems.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Details */}
            <h3 className="text-sm font-semibold text-foreground">Details</h3>

            <div className="space-y-2">
              <Label htmlFor="iss-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="iss-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary of the issue"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <RichTextEditor
                content={description}
                onChange={setDescription}
                placeholder="Describe the issue, steps to reproduce, expected vs actual..."
                minHeight={140}
                toolbarVariant="full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="iss-assignee">Assignee</Label>
              <select
                id="iss-assignee"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Unassigned</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Attachments — only once the issue exists (needs an entityId). */}
            {isEdit ? (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Attachments</h3>
                <FileUploadZone
                  entityType="issue"
                  entityId={editIssue!.id}
                  entityLabel={editIssue!.code}
                  productId={editIssue!.productId}
                  showExisting
                  compact
                  hideVaultActions
                />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                You can add attachments after saving the issue.
              </p>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || !productId || isPending}>
              {isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              {isEdit ? "Save Changes" : "Create Issue"}
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
